import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import CustomReactSelect from "components/CustomReactSelect";
import { useQueryClient } from "@tanstack/react-query";
import { useSparepartTagsList } from "api/inventorySpareparts";

const SparePartTagSelector = ({
    value,
    onChange,
    label = "Spare Part Tag",
    placeholder = "Select Tag...",
    isMulti = false,
    required = false,
    disabled = false,
    creatable = false,
    ...props
}) => {
    const queryClient = useQueryClient();
    const sparePartTagQuery = useSparepartTagsList();

    const sparePartTags = useMemo(
        () => (sparePartTagQuery.data ?? []).map(item => ({
            label: item,
            value: item,
        })),
        [sparePartTagQuery.data]
    );

    const handleFetch = useCallback(async (inputValue = "") => {
        const normalizedInput = inputValue.trim().toLowerCase();
        if (!normalizedInput) {
            return sparePartTags;
        }

        return sparePartTags.filter(option => option.label.toLowerCase().includes(normalizedInput));
    }, [sparePartTags]);

    const getSelectedValue = () => {
        if (!value) return null;
        if (isMulti) {
            return sparePartTags.filter(option => value.includes(option.value)) || null;
        }
        return sparePartTags.find(option => option.value === value) || null;
    };

    const handleChange = (selectedOption) => {
        if (isMulti) {
            const selectedValues = selectedOption ? selectedOption.map(opt => opt.value) : [];
            if (onChange) {
                onChange(selectedValues, selectedOption);
            }
        } else {
            const selectedValue = selectedOption ? selectedOption.value : null;
            if (onChange) {
                onChange(selectedValue, selectedOption);
            }
        }
    };

    const handleCreate = (inputValue) => {
        const newOption = {
            label: inputValue,
            value: inputValue
        };
        
        queryClient.setQueryData(['sparePartTags'], (oldTags = []) => {
            if (oldTags.includes(inputValue)) {
                return oldTags;
            }
            return [...oldTags, inputValue];
        });
        
        if (isMulti) {
            const currentValues = value || [];
            const newValues = [...currentValues, inputValue];
            if (onChange) {
                onChange(newValues, [...(getSelectedValue() || []), newOption]);
            }
        } else {
            if (onChange) {
                onChange(inputValue, newOption);
            }
        }
    };


    return (
        <CustomReactSelect
            label={label}
            isMulti={isMulti}
            isClearable
            cacheOptions={false}
            loadOptions={handleFetch}
            value={getSelectedValue()}
            options={sparePartTags}
            onChange={handleChange}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            noOptionsMessage={() => "No spare part tags found"}
            creatable={creatable}
            onCreateOption={creatable ? handleCreate : undefined}
            {...props}
        />
    );
};

SparePartTagSelector.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object,
        PropTypes.string,
        PropTypes.number,
    ]),
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    isMulti: PropTypes.bool,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    creatable: PropTypes.bool,
};

export default SparePartTagSelector;